import type { PerformanceInfoNote } from '@/app/lib/api/types-api'
import { sanitizeHtml } from '@/app/lib/utils/sanitizeHtml'

/** Aviso informativo listo para pintar: su HTML ya saneado. */
export interface PerformanceInfoNoteView {
  readonly id: string
  readonly descriptionHtml: string
  /** `start_at` válido en ISO (para `<time dateTime>`); `null` si no llega o es ilegible. */
  readonly updatedAtIso: string | null
  /** `dd/mm/aaaa` en hora de Madrid, formateado en servidor (sin desajuste de hidratación). */
  readonly updatedAtLabel: string | null
}

const DATE_FORMAT = new Intl.DateTimeFormat('es-ES', {
  timeZone: 'Europe/Madrid',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/**
 * Normaliza los avisos de la ficha (`info_notes`): sanea el HTML del CMS y
 * descarta los que se quedan en nada (el payload real manda notas con solo
 * `"\r\n"`).
 *
 * Vive aparte porque tiene **dos** consumidores que deben coincidir siempre:
 * `toPerformancePromoViewModel` —que las pinta en el bloque de promociones— y
 * `hasSpecialConditions` —que decide si el hero muestra el banner y la etiqueta
 * de condiciones especiales—. Si cada uno aplicara su propio criterio, el banner
 * podría anunciar «este evento tiene condiciones especiales» y llevar a un
 * bloque vacío, o al contrario.
 *
 * Filtra siempre por vigencia (FR-020, specs/016): se descarta la nota si
 * `start_at` es posterior a `now` o `end_at` es anterior a `now` (límites
 * inclusivos). Un límite nulo o ilegible significa «sin límite» y no oculta.
 * `now` por defecto es el instante actual; los consumidores le pasan el mismo
 * valor. La regla vive solo aquí para que bloque y banner no se desalineen.
 */
export function resolveInfoNotes(
  notes: readonly PerformanceInfoNote[] | null | undefined,
  options?: { readonly now?: Date },
): PerformanceInfoNoteView[] {
  const now = options?.now ?? new Date()
  return (notes ?? [])
    .filter((note) => isInRange(note.start_at, note.end_at, now))
    .map((note, index) => {
      const start = note.start_at ? new Date(note.start_at) : null
      const valid = start !== null && !Number.isNaN(start.getTime())
      return {
        id: `info-note-${index}`,
        descriptionHtml: sanitizeHtml(note.description ?? ''),
        updatedAtIso: valid ? start.toISOString() : null,
        updatedAtLabel: valid ? DATE_FORMAT.format(start) : null,
      }
    })
    .filter((note) => note.descriptionHtml !== '')
}

function isInRange(
  startAt: string | null | undefined,
  endAt: string | null | undefined,
  now: Date,
): boolean {
  const start = startAt ? Date.parse(startAt) : Number.NaN
  const end = endAt ? Date.parse(endAt) : Number.NaN
  if (!Number.isNaN(start) && start > now.getTime()) return false
  if (!Number.isNaN(end) && end < now.getTime()) return false
  return true
}
